/**
 * What the home shows while the tour is being opened from the "Finish
 * onboarding" card: nothing when it is quick, a quiet line when it is slow,
 * and a fallback with two ways on when it failed. The welcome dialog says the
 * same things in its own frame.
 */

import { PROMPT_DIALOG_STYLES } from "@/template-prompt/promptDialogStyles";
import { createPortal } from "react-dom";
import { dismissStart, useStartTour } from "./entry";

export const START_FAILURE = "We couldn't open the tour's canvas.";

export function StartNotice() {
  const { start, state } = useStartTour();
  if (state !== "slow" && state !== "failed") return null;

  return createPortal(
    <>
      <style>{PROMPT_DIALOG_STYLES}</style>
      <style>{NOTICE_STYLES}</style>
      {state === "slow" ? (
        <div
          className="_co_onb _co_tour_start_working"
          data-tour-start-working=""
          role="status"
        >
          <span className="_co_onb_dot _co_tour_start_dot" aria-hidden />
          <span>Still working…</span>
        </div>
      ) : (
        <div className="_co_onb _co_tour_start_scrim" role="presentation">
          <div
            className="_co_tour_start_card"
            data-tour-fallback=""
            role="alertdialog"
            aria-label="Guided tour"
          >
            <h1 className="_co_tour_start_heading">
              <span>That didn&apos;t work.</span>
            </h1>
            <p className="_co_tour_start_text">
              <span>{START_FAILURE}</span>
            </p>
            <div className="_co_tour_start_actions">
              <button
                type="button"
                className="_co_tour_start_quiet"
                data-tour-fallback-exit=""
                onClick={dismissStart}
              >
                <span>Not now</span>
              </button>
              <button
                type="button"
                className="_co_onb_finish"
                data-tour-retry=""
                onClick={start}
                autoFocus
                style={{ minHeight: 38, paddingLeft: 18, paddingRight: 18 }}
              >
                <span>Try again</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </>,
    document.body,
  );
}

const NOTICE_STYLES = `
  ._co_tour_start_working {
    position: fixed;
    left: 50%;
    bottom: 24px;
    transform: translateX(-50%);
    z-index: 60;
    display: inline-flex;
    align-items: center;
    gap: 8px;
    height: 32px;
    padding: 0 14px;
    border-radius: 999px;
    background: var(--co-paper);
    border: 1px solid var(--co-border);
    box-shadow: 0 1px 2px rgba(15, 12, 8, 0.06), 0 4px 14px rgba(15, 12, 8, 0.10);
    color: var(--co-fg-muted);
    font: 500 12px/1 ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Inter', 'Geist', sans-serif;
  }
  ._co_tour_start_dot {
    width: 6px;
    height: 6px;
    border-radius: 50%;
    background: var(--co-rust);
  }
  ._co_tour_start_scrim {
    position: fixed;
    inset: 0;
    z-index: 1000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 24px;
    background: rgba(15, 12, 8, 0.42);
  }
  .dark ._co_tour_start_scrim { background: rgba(0, 0, 0, 0.62); }
  ._co_tour_start_card {
    width: min(440px, 100%);
    box-sizing: border-box;
    padding: 26px 28px;
    display: flex;
    flex-direction: column;
    gap: 12px;
    background: var(--co-paper);
    color: var(--co-fg);
    border: 1px solid var(--co-border);
    border-radius: 18px;
    box-shadow: 0 1px 2px rgba(15, 12, 8, 0.06), 0 12px 36px rgba(15, 12, 8, 0.18);
    font-family: ui-sans-serif, -apple-system, BlinkMacSystemFont, 'Inter', 'Geist', sans-serif;
  }
  .dark ._co_tour_start_card { background: rgba(26, 26, 22, 0.98); }
  ._co_tour_start_heading {
    margin: 0;
    font-size: 24px;
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 500;
  }
  ._co_tour_start_text {
    margin: 0;
    font-size: 15px;
    line-height: 1.5;
    color: var(--co-fg-muted);
  }
  ._co_tour_start_actions {
    display: flex;
    align-items: center;
    justify-content: flex-end;
    gap: 14px;
    margin-top: 8px;
  }
  ._co_tour_start_quiet {
    appearance: none;
    background: none;
    border: none;
    padding: 2px 4px;
    font: inherit;
    font-size: 13px;
    color: var(--co-fg-muted);
    cursor: pointer;
    border-radius: 4px;
  }
  ._co_tour_start_quiet:hover { color: var(--co-fg); text-decoration: underline; text-underline-offset: 3px; }
  ._co_tour_start_quiet:focus-visible { outline: 2px solid var(--co-rust); outline-offset: 2px; }
`;
